import { displaySoftHyphens } from "#/_packages/umbrella/utils/soft-hyphens";
import { Link } from "@tanstack/react-router";
import { cn, tv } from "tailwind-variants";
import type { BreadCrumbsResponseItem } from "#/_generated/umbraco/delivery-api/models";
import { m } from "#/paraglide/messages";

export type MainContentContainer =
	| "alwaysContained"
	| "fluidToSM"
	| "fluidToMD"
	| "fluidToLG"
	| "fluidToXL"
	| "fluidToXXL"
	| "alwaysFluid";

export type ContainerAlignment = "start" | "center" | "end";
export type BreadcrumbIntent =
	| "primary"
	| "secondary"
	| "light"
	| "dark"
	| "white"
	| "black"
	| "success"
	| "warning"
	| "danger"
	| "info"
	| "none";

export type BreadcrumbsProps = {
	ancestors?: BreadCrumbsResponseItem[];
	ariaLabel?: string;
	className?: string;
	currentPage?: string;
	intent?: BreadcrumbIntent;
	mainContentContainer?: MainContentContainer;
	mainContentContainerAlignment?: ContainerAlignment;
};

const breadcrumbs = tv({
	slots: {
		container: "",
		link: "",
		currentPageSlot: "text-label",
	},
	variants: {
		mainContentContainer: {
			alwaysContained: { container: "container " },
			fluidToSM: { container: "container-sm" },
			fluidToMD: { container: "container-md" },
			fluidToLG: { container: "container-lg" },
			fluidToXL: { container: "container-xl" },
			fluidToXXL: { container: "container-xxl" },
			alwaysFluid: { container: "container-fluid" },
		},
		mainContentContainerAlignment: {
			start: { container: "container-start" },
			center: { container: "" },
			end: { container: "container-end" },
		},
		intent: {
			primary: {
				link: "text-brand-primary-dim hover:text-brand-primary-text",
				currentPageSlot: "text-brand-primary-text",
			},
			secondary: {
				link: "text-brand-secondary-dim hover:text-brand-secondary-text",
				currentPageSlot: "text-brand-secondary-text",
			},
			light: {
				link: "text-brand-dark-contrast/70 hover:text-brand-dark-contrast",
				currentPageSlot: "text-brand-dark-contrast",
			},
			dark: {
				link: "text-brand-light-contrast/70 hover:text-brand-light-contrast",
				currentPageSlot: "text-brand-light-contrast",
			},
			white: {
				link: "text-white/70 hover:text-white",
				currentPageSlot: "text-white",
			},
			black: {
				link: "text-black/70 hover:text-black",
				currentPageSlot: "text-black",
			},
			success: {
				link: "text-emerald-800 hover:text-emerald-900",
				currentPageSlot: "text-emerald-900",
			},
			warning: {
				link: "text-amber-900 hover:text-amber-950",
				currentPageSlot: "text-amber-950",
			},
			danger: {
				link: "text-rose-800 hover:text-rose-900",
				currentPageSlot: "text-rose-900",
			},
			info: {
				link: "text-cyan-800 hover:text-cyan-900",
				currentPageSlot: "text-cyan-900",
			},
			none: {
				link: "text-brand-primary-dim hover:text-brand-primary",
				currentPageSlot: "",
			},
		},
	},
});

export function Breadcrumbs({
	ancestors = [],
	ariaLabel = m.breadcrumbs(),
	className,
	currentPage,
	intent = "none",
	mainContentContainer = "alwaysContained",
	mainContentContainerAlignment = "center",
}: BreadcrumbsProps) {
	if (!ancestors.length && !currentPage) {
		return null;
	}

	const { container, link, currentPageSlot } = breadcrumbs({
		intent,
		mainContentContainer,
		mainContentContainerAlignment,
	});

	return (
		<nav className={cn("nav-breadcrumb", className)} aria-label={ariaLabel}>
			<div className={cn(container())}>
				<ol className="breadcrumb">
					{ancestors.map((item) => (
						<li key={item.id} id={item.id} className="breadcrumb-item">
							{item.url ? (
								<Link
									preload={"intent"}
									to={"/$"}
									params={{ _splat: item.url }}
									className={cn("link", link())}
								>
									{displaySoftHyphens(item.title)}
								</Link>
							) : (
								<span>{displaySoftHyphens(item.title)}</span>
							)}
						</li>
					))}
					{currentPage ? (
						<li className="breadcrumb-item active">
							<span className={cn(currentPageSlot())}>
								{displaySoftHyphens(currentPage)}
							</span>
						</li>
					) : null}
				</ol>
			</div>
		</nav>
	);
}
